/**
 * The model catalog.
 *
 * Every size here was MEASURED from the Hugging Face file tree on 2026-07-26,
 * not estimated. The sizes are the q4f16 text path only: decoder plus token
 * embeddings, excluding vision and audio encoders we never load. The picker
 * shows these numbers before a visitor commits to a download, which was a
 * design requirement, so a guess here would be a lie in the UI.
 *
 * Storage note: an origin quota of roughly 6 GB was measured on the reference
 * machine. Two large tiers plus a pack will start evicting. The UI must show
 * usage against quota and offer a way to clear the cache.
 */

export type Device = "webgpu" | "wasm" | "cpu";

export type DType =
  | "q1"
  | "q1f16"
  | "q2"
  | "q2f16"
  | "q4"
  | "q4f16"
  | "q8"
  | "fp16"
  | "fp32"
  | "auto";

export type ModelEntry = {
  key: string;
  /** Shown in the picker. */
  label: string;
  hfId: string;
  /** Measured bytes for the weights we actually fetch. */
  sizeBytes: number;
  /**
   * A single dtype, or one per ONNX component. Multi-component repos (Gemma 4)
   * split into decoder_model_merged plus a separate embed_tokens table, and
   * each needs naming.
   */
  dtype: DType | Record<string, DType>;
  /**
   * Where each component runs. A single device, or one per ONNX component.
   *
   * Defaults to webgpu everywhere. The per-component form is the escape hatch
   * for an operator the WebGPU kernel cannot execute: put that component on
   * "wasm" and leave the rest on the GPU. The Janus demo does exactly this,
   * running `prepare_inputs_embeds` on wasm and everything else on webgpu.
   */
  device?: Device | Record<string, Device>;
  /**
   * Which transformers.js class loads this.
   *
   * Defaults to the ordinary causal LM. `multimodality` means
   * `MultiModalityCausalLM` plus `AutoProcessor`: one transformer carrying a
   * text head AND an image head, so the same weights answer questions and draw
   * pictures. A different load path, not a different runtime, which is the
   * whole reason this route was chosen over a second image stack.
   */
  modelClass?: "causal-lm" | "multimodality";
  /** Qwen3.5 exposes enable_thinking in its chat template. */
  supportsThinkToggle: boolean;
  /** Offered as a download tier on the live site, vs bench-only. */
  shipped: boolean;
  /**
   * Set when this model is MEASURED not to run on WebGPU, with the reason in
   * visitor-facing words. Distinct from `shipped: false`, which is a product
   * decision. This one is a fact about the runtime, and the picker uses it to
   * refuse the download rather than let someone pay for a wall.
   */
  webgpuBlocked?: string;
  /**
   * Size of the single largest weight file, where measured. Used as a
   * conservative upper bound on the largest tensor when checking against the
   * GPU's storage-buffer binding ceiling. Left undefined rather than guessed.
   */
  largestFileBytes?: number;
  note: string;
};

export const MODELS: ModelEntry[] = [
  {
    key: "bonsai-t1.7b",
    label: "Ternary Bonsai 1.7B",
    hfId: "onnx-community/Ternary-Bonsai-1.7B-ONNX",
    sizeBytes: 470_403_173,
    dtype: "q2f16",
    supportsThinkToggle: false,
    shipped: true,
    largestFileBytes: 470_044_188,
    note: "Default. A 1.7B model in the same 470 MB as an 0.8B, because ternary weights cost about 1.6 bits each. Same family our own bench crowned best extractor at 27B.",
  },
  {
    key: "bonsai-1.7b-q1",
    label: "Bonsai 1.7B (1-bit)",
    hfId: "onnx-community/Bonsai-1.7B-ONNX",
    sizeBytes: 291_056_445,
    dtype: "q1",
    supportsThinkToggle: false,
    shipped: true,
    largestFileBytes: 290_552_764,
    note: "The lightest real model here: 1.7B parameters in 291 MB at one bit each. Expect some quality cost against the ternary build for 40% less download.",
  },
  {
    key: "gemma-3-1b",
    label: "Gemma 3 1B",
    hfId: "onnx-community/gemma-3-1b-it-ONNX",
    sizeBytes: 763_529_245,
    dtype: "q4f16",
    supportsThinkToggle: false,
    shipped: true,
    largestFileBytes: 763_067_904,
    note: "A real Gemma, single-file text export, no vision or audio weight. Google's instruction tuning is a different flavour from Qwen's.",
  },
  {
    key: "qwen3.5-0.8b",
    label: "Qwen3.5 0.8B",
    hfId: "onnx-community/Qwen3.5-0.8B-Text-ONNX",
    sizeBytes: 469_929_410,
    dtype: "q4f16",
    supportsThinkToggle: true,
    shipped: true,
    largestFileBytes: 469_331_968,
    note: "Single-file text export. Its instruction-following is the weak point: it drifts off the retrieved facts unless the prompt binds it hard.",
  },
  {
    key: "qwen3.5-2b",
    label: "Qwen3.5 2B",
    hfId: "onnx-community/Qwen3.5-2B-ONNX",
    // decoder_model_merged_q4f16 1.09 GB + embed_tokens_q4f16 294 MB.
    sizeBytes: 1_383_788_544,
    // Multi-component, like Gemma 4. This was a plain "q4f16" until preflight
    // caught it: that names onnx/model_q4f16.onnx, which does not exist in this
    // repo, so the tier was offered in the picker and could never have loaded.
    // The vision encoder is deliberately unnamed so the loader never fetches it.
    dtype: { decoder_model_merged: "q4f16", embed_tokens: "q4f16" },
    supportsThinkToggle: true,
    shipped: true,
    largestFileBytes: 1_089_777_664,
    // RUN FOR THE FIRST TIME 2026-07-26, after the dtype fix. It works.
    //
    //   model          load s   tok/s   TTFT s
    //   Gemma 3 1B        3.9    33.7      0.7
    //   Qwen 3.5 2B       5.5    17.4      5.5
    //
    // Three times slower end to end than Gemma 3 1B for the same length of
    // answer, and the cost is almost all PREFILL: eight times the time to the
    // first token, against only twice the decode time. `converse()` resets the
    // KV cache before every generation by design, so that is paid on every
    // turn rather than once.
    note: "Opt in, and slow to start: 5.5s before its first token against Gemma 3 1B's 0.7s, paid every turn. Decoder plus embeddings; the vision encoder is not loaded.",
  },
  {
    key: "bonsai-t4b",
    label: "Ternary Bonsai 4B",
    hfId: "onnx-community/Ternary-Bonsai-4B-ONNX",
    sizeBytes: 1_086_827_330,
    dtype: "q2f16",
    supportsThinkToggle: false,
    shipped: true,
    largestFileBytes: 1_086_368_163,
    note: "The middle of the Bonsai range: 4B parameters in 1.09 GB, half the 8B's download. It says more per answer than the 1.7B and is fractionally less careful about it.",
  },
  {
    key: "bonsai-4b-q1",
    label: "Bonsai 4B (1-bit)",
    hfId: "onnx-community/Bonsai-4B-ONNX",
    sizeBytes: 655_915_713,
    dtype: "q1",
    supportsThinkToggle: false,
    // CANNOT RUN. Measured 2026-07-26: BOTH published variants die at session
    // creation with
    //   Can't create a session. ERROR_CODE: 6, ERROR_MESSAGE: std::bad_alloc
    // q1 (655 MB) and q1f16 (584 MB) fail identically, reproducibly, on a fresh
    // page with no other model resident, so it is the export rather than the
    // dtype and it is not memory pressure from a previous model.
    //
    // Note the contrast: Bonsai 1.7B q1 runs fine, and Ternary Bonsai 4B q2f16
    // runs fine at nearly twice the download. It is specifically the 1-bit 4B.
    //
    // std::bad_alloc is a WASM-side allocation failure during graph
    // preparation, which is a failure class `preflight.mjs` CANNOT detect: that
    // tool reads operator bit widths, not memory behaviour. Preflight cleared
    // this model. Loading is still not proof, and neither is preflight.
    shipped: false,
    webgpuBlocked:
      "This build runs out of memory while starting up, before it can answer " +
      "anything. Both published versions do it. Ternary Bonsai 4B is the one " +
      "to use at this size.",
    largestFileBytes: 655_270_315,
    note: "Does not run: both the 655 MB and 584 MB builds fail session creation with std::bad_alloc. Kept as a documented dead end, and as the reminder that preflight cannot catch everything.",
  },
  {
    key: "bonsai-t8b",
    label: "Ternary Bonsai 8B",
    hfId: "onnx-community/Ternary-Bonsai-8B-ONNX",
    sizeBytes: 2_210_042_236,
    dtype: "q2f16",
    supportsThinkToggle: false,
    shipped: true,
    largestFileBytes: 2_043_353_232,
    note: "Opt in, and the most brain per byte on offer: an 8B model in 2.2 GB.",
  },
  {
    key: "qwen3.5-4b",
    label: "Qwen3.5 4B",
    hfId: "onnx-community/Qwen3.5-4B-ONNX",
    sizeBytes: 2_803_656_491,
    // Multi-component, same correction as the 2B above.
    dtype: { decoder_model_merged: "q4f16", embed_tokens: "q4f16" },
    supportsThinkToggle: true,
    shipped: true,
    largestFileBytes: 2_067_208_192,
    // RUN FOR THE FIRST TIME 2026-07-26. It works, but only with room.
    //
    //   clean cache, alone:   load 15.6s, 13.4 tok/s, TTFT 9.8s
    //   with 2 models cached: LOAD TIMED OUT at 300s
    //
    // The timeout is a QUOTA artefact, not a model defect: 2.8 GB does not fit
    // alongside two other models on the 5.96 GB quota measured here, so the
    // weight cache thrashes (usage swung 3.19 GB -> 0.11 -> 0.97 mid-download)
    // and the load never finishes. `willFit()` exists for this and is called on
    // the download path, but not by a direct `llm.load()`, which is how it went
    // unnoticed.
    //
    // And it is specifically the PANE's quota. Real Chrome on the same machine
    // reports 11-13 GB, and its Cache API stores a 4 GiB entry without
    // complaint, so this model's 2.07 GB decoder is not close to any per-entry
    // limit there. Read the timeout as a fact about a 5.96 GB browser, not
    // about the model.
    //
    // Nearly ten seconds before the first token, on every turn, because
    // `converse()` resets the KV cache each generation. Fourteen times Gemma 3
    // 1B's 0.7s.
    note: "Opt in, heavy, and slow to start: about 10s before its first token, paid every turn. Needs most of the storage quota to itself; alongside two other models its load times out.",
  },
  {
    key: "gemma-3-270m",
    label: "Gemma 3 270M",
    hfId: "onnx-community/gemma-3-270m-it-ONNX",
    sizeBytes: 272_956_893,
    dtype: "q4f16",
    supportsThinkToggle: false,
    shipped: true,
    largestFileBytes: 272_626_176,
    note: "The floor: 273 MB. Useful precisely because it shows where grounding breaks down entirely.",
  },
  {
    key: "gemma-4-e2b-qat",
    label: "Gemma 4 E2B (QAT)",
    hfId: "onnx-community/gemma-4-E2B-it-qat-mobile-ONNX",
    sizeBytes: 2_291_998_235,
    // Multi-component: the decoder and a separate 1.30 GB embedding table.
    // Vision and audio encoders exist in the repo and are deliberately unnamed
    // so the loader never fetches them.
    dtype: { decoder_model_merged: "q2f16", embed_tokens: "q2f16" },
    // THE SHIM, TRIED AND MEASURED. It does not work, and the way it fails is
    // the useful part.
    //
    // Idea: the WebGPU kernel refuses a 2-bit embedding gather, but ONNX
    // Runtime's CPU kernel accepts one. Its source validates "uint8 data only
    // supports bits==2, 4, or 8" and ships a Get2BitElementUint8 helper. So put
    // that one component on wasm and leave the decoder, where all the compute
    // is, on the GPU. transformers.js supports exactly this: `device` takes a
    // per-component map, which is how the Janus demo runs prepare_inputs_embeds
    // on wasm and everything else on webgpu.
    //
    // The placement works. The error changes, which proves the op really moved:
    //   webgpu:  GatherBlockQuantized: bits_ == 4 || bits_ == 8 was false
    //   wasm:    Failed to find kernel for com.microsoft.GatherBlockQuantized(1)
    //            (node:'/model.embed_tokens/GatherBlockQuantized'
    //             ep:'CPUExecutionProvider'). Kernel not found
    //
    // So ORT Web's WASM build simply does not COMPILE IN that CPU kernel, even
    // though it exists upstream and handles 2 bits. Both browser providers fail
    // for opposite reasons: WebGPU has the kernel and refuses the bit width,
    // wasm would accept the bit width and lacks the kernel. There is no shim
    // from here. The fixes are upstream (ORT widens the WebGPU kernel or ships
    // the CPU one to wasm) or offline graph surgery to requantize the table to
    // 4 bits, which would roughly double it and defeat the point of the build.
    device: { embed_tokens: "wasm", decoder_model_merged: "webgpu" },
    supportsThinkToggle: false,
    // CANNOT RUN ON WEBGPU. Measured 2026-07-26: all four files download and
    // the session then fails to create with
    //   GatherBlockQuantized: bits_ == 4 || bits_ == 8 was false
    //
    // Reading the bit-width attributes straight out of the graphs shows the
    // real rule, which is narrower and more useful than "2-bit is unsupported":
    //
    //   model                       gather bits    matmul bits
    //   Ternary Bonsai 1.7B q2f16        4          2  (x197)   runs
    //   Bonsai 1.7B q1                   4          2  (x197)   runs
    //   Gemma 3 1B q4f16                 4          4  (x183)   runs
    //   Gemma 4 E2B QAT embed_tokens     2            -          FAILS
    //
    // The embedding gather's bit width is an INDEPENDENT dial from the matmul
    // bit width, and only the gather is pinned to 4 or 8 by the WebGPU kernel.
    // Every model that runs keeps its gather at 4 while pushing matmuls to 2.
    // Bonsai advertises 1.58 bits "throughout", but its export does not do that
    // to the embedding table, which is exactly why it works here.
    //
    // The repo publishes no other embed_tokens variant, so there is no dtype
    // mix that avoids it. `tools/preflight.mjs` now checks this from the graph
    // file alone, before any weights are fetched. Revisit when ORT Web widens
    // that kernel.
    shipped: false,
    webgpuBlocked:
      "Its embedding table is 2-bit. The browser's GPU can only read 4-bit or " +
      "8-bit tables, and the CPU fallback for that operation is not included " +
      "in the browser build. Nothing about your device: no browser can run " +
      "this build today.",
    largestFileBytes: 1_296_564_224,
    note: "Does not run in any browser: its 2-bit embedding lookup has no kernel on either the GPU or the CPU side of ONNX Runtime Web. Kept as a documented dead end.",
  },
  {
    key: "gemma-4-e2b",
    label: "Gemma 4 E2B",
    hfId: "onnx-community/gemma-4-E2B-it-ONNX",
    // decoder_model_merged_q4f16 1.52 GB + embed_tokens_q4f16 1.59 GB.
    // The embedding half is enormous because Gemma 4 E2B carries per-layer
    // embeddings, not just a vocab table, so it does not shrink the way a
    // conventional embedding matrix would.
    sizeBytes: 3_110_390_784,
    dtype: { decoder_model_merged: "q4f16", embed_tokens: "q4f16" },
    supportsThinkToggle: false,
    shipped: false,
    largestFileBytes: 1_590_689_792,
    note: "Bench only, and heavy: the ONLY Gemma 4 route WebGPU can actually execute, because 4-bit embedding gather is supported where 2-bit is not. Must earn 4x Gemma 3 1B's download to ship.",
  },
  {
    key: "janus-pro-1b",
    label: "Janus Pro 1B (chat + images)",
    hfId: "onnx-community/Janus-Pro-1B-ONNX",
    // Sum of the six components below, measured from the file tree.
    sizeBytes: 2_527_264_768,
    /**
     * SIX components, and the recipe is copied from the working
     * webml-community/janus-pro-webgpu demo rather than guessed. Each head has
     * its own precision because they have different jobs:
     *
     *   prepare_inputs_embeds  q4    1038.8 MB   the embedding table
     *   language_model         q4f16  698.2 MB   the transformer
     *   lm_head                fp16   419.4 MB   the TEXT head
     *   gen_head               fp16    75.5 MB   the IMAGE head
     *   gen_img_embeds         fp16     8.7 MB
     *   image_decode           fp32   170.5 MB   tokens back to pixels
     *
     * `image_decode` stays fp32 deliberately: it is the last stage before
     * pixels, it is small, and quantising it shows up directly as artefacts.
     */
    dtype: {
      prepare_inputs_embeds: "q4",
      language_model: "q4f16",
      lm_head: "fp16",
      gen_head: "fp16",
      gen_img_embeds: "fp16",
      image_decode: "fp32",
    },
    /**
     * `prepare_inputs_embeds` runs on WASM, everything else on the GPU.
     *
     * Not a workaround for a missing kernel this time: it is what the working
     * demo does, and the embedding gather is memory-bound rather than
     * compute-bound, so the CPU is a reasonable place for it while the
     * transformer keeps the GPU.
     */
    device: {
      prepare_inputs_embeds: "wasm",
      language_model: "webgpu",
      lm_head: "webgpu",
      gen_head: "webgpu",
      gen_img_embeds: "webgpu",
      image_decode: "webgpu",
    },
    modelClass: "multimodality",
    supportsThinkToggle: false,
    // BENCHED 2026-07-26, and the answer is decisive: IMAGE ENGINE ONLY.
    //
    //   model          MB   grounded  claims  invented  silent  chars  sec
    //   Gemma 3 1B    764       90%      10         1       0    388  2.9
    //   Janus Pro 1B 2410       25%       4         3       1    198  2.1
    //
    // 25% is the worst grounding of any model measured here, below even Qwen
    // 0.8B's 38%, for 3.2x Gemma 3 1B's download.
    //
    // And it is NOT a plumbing failure, which was the obvious suspicion. The
    // aperture returned 6 correct facts on every question and the right answer
    // was in the context every time: the `@container` at-rule, `createBindGroup()`
    // on GPUDevice, `readPixels` with its inverted pixelY. Janus ignored all of
    // it and produced "Bootstrap®", "sidebyside" and "getPixelData()". The
    // lattice delivered; the model could not read it.
    //
    // Only 4 extractable claims across 5 questions, against Gemma 3 1B's 10,
    // so it also barely asserts anything checkable. Its text head is small and
    // its grammar shows it ("A CSS container refers specifically used within").
    shipped: false,
    largestFileBytes: 1_089_255_424,
    note: "IMAGE ENGINE, not a chat tier: 25% grounded against Gemma 3 1B's 90%, with the correct facts in its context every time. Images are good, 384x384 in about 13s.",
  },
];

/**
 * LIGHTER_GEMMA, found 2026-07-26 by Free spotting that the webml-community
 * kernels demo pulls far less than our measured ONNX figure.
 *
 * That demo does not use ONNX at all. It fetches google/gemma-4-E2B-it-qat-
 * mobile-transformers, a quantization-aware-trained int4 checkpoint whose
 * model.safetensors is 2.46 GB, and then range-fetches only the tensors it
 * needs. Confirmed by reading its source: it parses the safetensors header
 * for data_offsets and issues HTTP Range requests, skipping the vision and
 * audio towers entirely. Transfer lands near 1.9 GB.
 *
 * Two things worth keeping:
 *
 * 1. QAT int4 beats post-training quantization on both size and quality, so
 *    a QAT checkpoint is the right artifact for a browser when one exists.
 * 2. Range-fetching selected tensors out of a safetensors file is a general
 *    technique, independent of runtime, and would help any multimodal
 *    checkpoint where we only want the language tower.
 *
 * We are NOT switching to that runtime. It is a single 552 kB bespoke file
 * with hand-written WGSL, sdk: static, one supported model, and no stable
 * API for the two things this project depends on: past_key_values reuse for
 * the tool loop, and a Node-side twin for headless benching. The trade is
 * download size against the entire testing strategy, and the default tier is
 * a 470 MB Qwen either way, which is smaller than both Gemma routes.
 *
 * Revisit if transformers.js gains QAT-mobile support, or if onnx-community
 * publishes a text-only Gemma 4 export.
 */
export const GEMMA_QAT_MOBILE = {
  hfId: "google/gemma-4-E2B-it-qat-mobile-transformers",
  safetensorsBytes: 2_458_111_846,
  approxTextOnlyTransferBytes: 1_900_000_000,
} as const;

/**
 * Context cap. The config allows 131072, but long context costs KV cache
 * VRAM at runtime, not download size, and a retrieval-backed mind wants a
 * small window by design: coherence comes from the substrate, not the window.
 * The pocket spec is 4-8K and that is what we budget for.
 */
export const MAX_CONTEXT_TOKENS = 8192;

/**
 * Gemma 3 1B, on the seven-model sweep of 2026-07-26 (see docs/BENCH.md).
 *
 * It was Ternary Bonsai 1.7B. The measurement moved it:
 *
 *   model                 MB   grounded   silent   chars   sec
 *   Gemma 3 1B           764       90%        0     388    2.9
 *   Ternary Bonsai 1.7B  470       82%        1     696    4.7
 *
 * 294 MB more buys 8 points of grounding, answers 44% shorter, generation 38%
 * faster, and the only zero in the "silent" column: every other model in the
 * sweep produced at least one answer that asserted nothing checkable, and
 * Gemma 3 1B never did. Ternary Bonsai 1.7B remains a first-class tier and is
 * still the right pick for anyone watching their bandwidth.
 *
 * Reverting is this one line.
 */
export const DEFAULT_MODEL_KEY = "gemma-3-1b";

export function modelByKey(key: string): ModelEntry {
  const m = MODELS.find((x) => x.key === key);
  if (!m) throw new Error(`unknown model key: ${key}`);
  return m;
}

export const SHIPPED_MODELS = MODELS.filter((m) => m.shipped);
